"use client";

import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getTrialUsageLimit } from "@/actions/settings/trialUsage/actions";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { SubscriptionStatus } from "@/enums/stripe";
import { TrialUsageLimit } from "@/types/org";
import { TRIAL_LIMIT_EVENT } from "@/lib/api";

/**
 * Stable flag-like fields — these change rarely so consumers that only care
 * about "is the widget visible?" or "did we hit the limit?" don't re-render
 * every polling tick.
 */
type TrialUsageFlags = {
  isLoading: boolean;
  isLimitReached: boolean;
  isNear: boolean;
  shouldShow: boolean;
  refetch: () => Promise<void>;
};

/**
 * Numeric/data fields — these can change on every tick. Kept in a separate
 * context so consumers that render numeric bars subscribe without forcing
 * flag-only consumers to re-render.
 */
type TrialUsageValues = {
  usage: TrialUsageLimit | null;
  callsPercent: number;
  conversationsPercent: number;
  usagePercent: number;
};

type TrialUsageState = TrialUsageFlags & TrialUsageValues;

const DEFAULT_FLAGS: TrialUsageFlags = {
  isLoading: false,
  isLimitReached: false,
  isNear: false,
  shouldShow: false,
  refetch: async () => {},
};

const DEFAULT_VALUES: TrialUsageValues = {
  usage: null,
  callsPercent: 0,
  conversationsPercent: 0,
  usagePercent: 0,
};

const TrialUsageFlagsContext = createContext<TrialUsageFlags>(DEFAULT_FLAGS);
const TrialUsageValuesContext = createContext<TrialUsageValues>(DEFAULT_VALUES);

/** Polling interval while the widget is visible — keeps the bar reasonably fresh. */
const REFETCH_INTERVAL_MS = 60_000;
/** Multiplier applied each time we skip a fetch because the tab is hidden, capped. */
const HIDDEN_BACKOFF_MAX_MS = 5 * 60_000;

function usageEqual(a: TrialUsageLimit | null, b: TrialUsageLimit | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.totalCallSeconds === b.totalCallSeconds &&
    a.totalConversations === b.totalConversations &&
    a.maxTotalCallSeconds === b.maxTotalCallSeconds &&
    a.maxTotalConversations === b.maxTotalConversations &&
    a.isLimitReached === b.isLimitReached &&
    a.isEnforced === b.isEnforced &&
    a.isTrialing === b.isTrialing
  );
}

function computePercents(usage: TrialUsageLimit | null) {
  if (!usage) {
    return {
      callsPercent: 0,
      conversationsPercent: 0,
      usagePercent: 0,
      isLimitReached: false,
      isNear: false,
    };
  }

  const safeMaxCalls = usage.maxTotalCallSeconds > 0 ? usage.maxTotalCallSeconds : 1;
  const safeMaxConvos = usage.maxTotalConversations > 0 ? usage.maxTotalConversations : 1;
  const callsPercent = Math.min(100, (usage.totalCallSeconds / safeMaxCalls) * 100);
  const conversationsPercent = Math.min(
    100,
    (usage.totalConversations / safeMaxConvos) * 100
  );
  const usagePercent = Math.max(callsPercent, conversationsPercent);

  return {
    callsPercent,
    conversationsPercent,
    usagePercent,
    isLimitReached: usage.isLimitReached,
    isNear: usagePercent >= 80,
  };
}

export const TrialUsageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { subscription } = useAuthContext();
  const [usage, setUsageState] = useState<TrialUsageLimit | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isLimitReached = !!usage?.isLimitReached;

  const isTrialingFromAuth = subscription?.status === SubscriptionStatus.trialing;

  const refetch = useCallback(async () => {
    if (!isTrialingFromAuth) {
      setUsageState((prev) => (prev === null ? prev : null));
      return;
    }
    setIsLoading(true);
    try {
      const data = await getTrialUsageLimit();
      setUsageState((prev) => (usageEqual(prev, data) ? prev : data));
    } finally {
      setIsLoading(false);
    }
  }, [isTrialingFromAuth]);

  // Polling with Page Visibility gate and stop-on-limit. Once the limit has
  // been reached the server state can't advance without a user action
  // (upgrade / billing event), so we stop the timer to avoid burning quota.
  useEffect(() => {
    if (!isTrialingFromAuth) {
      setUsageState((prev) => (prev === null ? prev : null));
      return;
    }
    if (isLimitReached) {
      return;
    }

    refetch();

    const getDocumentHidden = () =>
      typeof document !== "undefined" && document.visibilityState === "hidden";

    let hiddenSkipCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const schedule = (delay: number) => {
      timeoutId = setTimeout(async () => {
        if (getDocumentHidden()) {
          hiddenSkipCount += 1;
          // Exponential-ish backoff while hidden; capped to HIDDEN_BACKOFF_MAX_MS.
          const next = Math.min(REFETCH_INTERVAL_MS * 2 ** hiddenSkipCount, HIDDEN_BACKOFF_MAX_MS);
          schedule(next);
          return;
        }
        hiddenSkipCount = 0;
        await refetch();
        schedule(REFETCH_INTERVAL_MS);
      }, delay);
    };

    const onVisibilityChange = () => {
      if (!getDocumentHidden()) {
        hiddenSkipCount = 0;
        refetch();
      }
    };

    schedule(REFETCH_INTERVAL_MS);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [isTrialingFromAuth, isLimitReached, refetch]);

  // Transport-layer interceptor: whenever any apiFetchWithStatus call gets
  // back a trial-limit 403, refetch so the UI reflects the latest snapshot.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      refetch();
    };
    window.addEventListener(TRIAL_LIMIT_EVENT, handler);
    return () => window.removeEventListener(TRIAL_LIMIT_EVENT, handler);
  }, [refetch]);

  const values = useMemo<TrialUsageValues>(() => {
    const { callsPercent, conversationsPercent, usagePercent } = computePercents(usage);
    return { usage, callsPercent, conversationsPercent, usagePercent };
  }, [usage]);

  const flags = useMemo<TrialUsageFlags>(() => {
    const { isLimitReached: computedLimit, isNear } = computePercents(usage);
    const shouldShow = !!usage && usage.isTrialing && usage.isEnforced;
    return {
      isLoading,
      isLimitReached: computedLimit,
      isNear,
      shouldShow,
      refetch,
    };
  }, [usage, isLoading, refetch]);

  return (
    <TrialUsageFlagsContext.Provider value={flags}>
      <TrialUsageValuesContext.Provider value={values}>
        {children}
      </TrialUsageValuesContext.Provider>
    </TrialUsageFlagsContext.Provider>
  );
};

/** Full state — both stable flags and fresh numeric values. */
export const useTrialUsage = (): TrialUsageState => {
  const flags = useContext(TrialUsageFlagsContext);
  const values = useContext(TrialUsageValuesContext);
  return { ...flags, ...values };
};

/** Stable flags only (refetch / isLimitReached / shouldShow / isNear / isLoading). */
export const useTrialUsageFlags = (): TrialUsageFlags =>
  useContext(TrialUsageFlagsContext);

/** Numeric values only — opt in to per-tick re-renders. */
export const useTrialUsageValues = (): TrialUsageValues =>
  useContext(TrialUsageValuesContext);
